import { BreakdownView } from "@/components/Pipeline/BreakdownView";
import { PipelineHeader, PipelineTab } from "@/components/Pipeline/PipelineHeader";
import { SummaryView } from "@/components/Pipeline/SummaryView";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useAppData } from "@/context/AppDataContext";
import {
  AgentPipelineModel,
  AllDepartmentsPipelineModel,
  CustomerModel,
  DashboardPipelineModel,
  DashboardPipelineStatistics,
  DepartmentPipelineModel,
  Handover,
  SalesHierarchyModel,
  ValCallModel,
  WIPModel,
} from "@/models/pipeline";
import { pipelineService } from "@/services/PipelineService";
import {
  DEFAULT_BUSINESS_OPTIONS,
  mockDirectorOverviewData,
  mockHoBDetailData,
  mockOSMData,
  mockTeamData,
} from "@/services/mockData";
import { PipelineLevel } from "@/types/pipeline";
import { CircleX } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isOfflineMode } from "@/helpers/offlineMode";

type PipelineData =
  | { type: "agent"; data: AgentPipelineModel }
  | { type: "department"; data: DepartmentPipelineModel }
  | { type: "all"; data: AllDepartmentsPipelineModel };

const DEALER_DIRECT_HOS_ID = 1848;
const PIPELINE_VIEW_TAB_KEY = "pipeline_view_tab";
type BusinessFilterOption = "Whole Business" | "Consumer Direct" | "Dealer Direct";

export const Pipeline = () => {
  const {
    workers,
    workerOptions,
    departmentOptions,
    currentUser,
    salesHierarchy,
    isLoading: isAppDataLoading,
  } = useAppData();
  const [isPipelineLoading, setIsPipelineLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [activeTab, setActiveTab] = useState<PipelineTab>(() => {
    const stored = sessionStorage.getItem(PIPELINE_VIEW_TAB_KEY);
    return stored === "summary" || stored === "breakdown" ? stored : "summary";
  });
  const [selectedHeadOfBusiness, setSelectedHeadOfBusiness] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [selectedOSM, setSelectedOSM] = useState<string | null>(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null);
  const [selectedAgentId, setSelectedAgentId] = useState<number>(-1);
  const [selectedBusiness, setSelectedBusiness] = useState<BusinessFilterOption>(
    DEFAULT_BUSINESS_OPTIONS[0].value as BusinessFilterOption
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date>(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [pipelineData, setPipelineData] = useState<PipelineData | null>(null);
  const [pinnedApplications, setPinnedApplications] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [hobOverviewData, setHobOverviewData] = useState<DashboardPipelineModel | null>(null);
  const [hobDetailData, setHobDetailData] = useState<DashboardPipelineModel | null>(null);
  const [osmOverviewData, setOsmOverviewData] = useState<DashboardPipelineModel | null>(null);
  const [teamOverviewData, setTeamOverviewData] = useState<DashboardPipelineModel | null>(null);
  const [isDashboardLoading, setIsDashboardLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const hobAbortControllerRef = useRef<AbortController | null>(null);
  const hobDetailAbortControllerRef = useRef<AbortController | null>(null);
  const osmAbortControllerRef = useRef<AbortController | null>(null);
  const teamAbortControllerRef = useRef<AbortController | null>(null);

  const currentAgentId = currentUser?.userId ?? -1;
  const currentAgentDepartmentId = currentUser?.departmentId ?? "";

  useEffect(() => {
    if (currentUser && !isInitialized) {
      setIsInitialized(true);
    }
  }, [currentUser, isInitialized]);

  const isLoading = isAppDataLoading || !isInitialized || isPipelineLoading;

  const normalizedSelectedAgentId = selectedAgentId ?? -1;

  const getOSMForDepartment = useCallback(
    (departmentId: string | null) => {
      if (!departmentId || departmentId === "all") return null;
      const hierarchy = salesHierarchy.find((h) => h.departmentId === departmentId);
      return hierarchy?.osmName ?? null;
    },
    [salesHierarchy]
  );

  const getHoBForOSM = useCallback(
    (osmName: string | null) => {
      if (!osmName) return null;
      const hierarchy = salesHierarchy.find((h) => h.osmName === osmName);
      if (!hierarchy) return null;
      return { id: hierarchy.hosId, name: hierarchy.hosName };
    },
    [salesHierarchy]
  );

  const selectedOSMHierarchy = useMemo(() => {
    if (!selectedOSM) return null;
    return (
      salesHierarchy.find(
        (h) => h.osmName === selectedOSM && (!selectedHeadOfBusiness || h.hosId === selectedHeadOfBusiness.id)
      ) ?? null
    );
  }, [salesHierarchy, selectedOSM, selectedHeadOfBusiness]);

  const currentLevel = useMemo(() => {
    if (normalizedSelectedAgentId !== -1) {
      return PipelineLevel.Agent;
    }

    if (selectedDepartmentId && selectedDepartmentId !== "all") {
      return PipelineLevel.Team;
    }

    if (selectedOSM) {
      return PipelineLevel.OSM;
    }

    if (selectedHeadOfBusiness) {
      return PipelineLevel.HoB;
    }

    return PipelineLevel.Director;
  }, [normalizedSelectedAgentId, selectedOSM, selectedHeadOfBusiness, selectedDepartmentId]);

  const shouldShowDirectorView = currentLevel === PipelineLevel.Director;
  const shouldShowHoBView = currentLevel === PipelineLevel.HoB;
  const shouldShowOSMView = currentLevel === PipelineLevel.OSM;
  const shouldShowTeamView = currentLevel === PipelineLevel.Team;

  const isCurrentAgentView = useMemo(() => {
    return selectedAgentId === currentAgentId;
  }, [selectedAgentId, currentAgentId]);

  const fetchPipelineData = useCallback(async () => {
    if (!pipelineService.isReady()) {
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    setIsPipelineLoading(true);
    setError(null);

    try {
      if (normalizedSelectedAgentId !== -1) {
        const data = await pipelineService.getAgentPipeline(normalizedSelectedAgentId, signal);
        setPipelineData({ type: "agent", data });
      } else if (selectedDepartmentId && selectedDepartmentId !== "all") {
        const data = await pipelineService.getDepartmentPipeline(selectedDepartmentId, signal);
        setPipelineData({ type: "department", data });
      }
    } catch (err) {
      if (err instanceof Error && err.name === "CanceledError") {
        return;
      }
      console.error("Failed to fetch pipeline data:", err);
      setError("Failed to load pipeline data.");
    } finally {
      setIsPipelineLoading(false);
    }
  }, [normalizedSelectedAgentId, selectedDepartmentId]);

  useEffect(() => {
    if (!isInitialized) return;

    fetchPipelineData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [isInitialized, fetchPipelineData]);

  const fetchDirectorData = useCallback(async () => {
    if (!pipelineService.isReady()) return;

    if (isOfflineMode()) {
      setHobOverviewData(mockDirectorOverviewData);
      return;
    }

    if (hobAbortControllerRef.current) {
      hobAbortControllerRef.current.abort();
    }

    hobAbortControllerRef.current = new AbortController();
    const { signal } = hobAbortControllerRef.current;

    setIsDashboardLoading(true);
    try {
      const data = await pipelineService.getDirectorOverview(formatDateOnly(selectedDate), signal);
      setHobOverviewData(data);
    } catch (err) {
      if (err instanceof Error && err.name === "CanceledError") return;
      console.error("Failed to fetch HoB overview:", err);
    } finally {
      setIsDashboardLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (!isInitialized || !shouldShowDirectorView) return;

    fetchDirectorData();

    return () => {
      if (hobAbortControllerRef.current) {
        hobAbortControllerRef.current.abort();
      }
    };
  }, [isInitialized, fetchDirectorData, shouldShowDirectorView]);

  const fetchHoBData = useCallback(async () => {
    if (!pipelineService.isReady() || !selectedHeadOfBusiness) return;

    if (isOfflineMode()) {
      setHobDetailData(mockHoBDetailData[selectedHeadOfBusiness.name] ?? null);
      return;
    }

    if (hobDetailAbortControllerRef.current) {
      hobDetailAbortControllerRef.current.abort();
    }

    hobDetailAbortControllerRef.current = new AbortController();
    const { signal } = hobDetailAbortControllerRef.current;

    setIsDashboardLoading(true);
    try {
      const data = await pipelineService.getHobOverview(selectedHeadOfBusiness.id, formatDateOnly(selectedDate), signal);
      setHobDetailData(data);
    } catch (err) {
      if (err instanceof Error && err.name === "CanceledError") return;
      console.error("Failed to fetch HoB detail:", err);
    } finally {
      setIsDashboardLoading(false);
    }
  }, [selectedHeadOfBusiness, selectedDate]);

  useEffect(() => {
    if (!isInitialized || !shouldShowHoBView) {
      setHobDetailData(null);
      return;
    }

    fetchHoBData();

    return () => {
      if (hobDetailAbortControllerRef.current) {
        hobDetailAbortControllerRef.current.abort();
      }
    };
  }, [isInitialized, fetchHoBData, shouldShowHoBView]);

  const fetchOSMData = useCallback(async () => {
    if (!pipelineService.isReady() || !selectedOSMHierarchy) return;

    if (isOfflineMode()) {
      setOsmOverviewData(selectedOSM ? (mockOSMData[selectedOSM] ?? null) : null);
      return;
    }

    if (osmAbortControllerRef.current) {
      osmAbortControllerRef.current.abort();
    }

    osmAbortControllerRef.current = new AbortController();
    const { signal } = osmAbortControllerRef.current;

    setIsDashboardLoading(true);
    try {
      const data = await pipelineService.getOsmOverview(selectedOSMHierarchy.osmId, formatDateOnly(selectedDate), signal);
      setOsmOverviewData(data);
    } catch (err) {
      if (err instanceof Error && err.name === "CanceledError") return;
      console.error("Failed to fetch OSM overview:", err);
    } finally {
      setIsDashboardLoading(false);
    }
  }, [selectedDate, selectedOSM, selectedOSMHierarchy]);

  useEffect(() => {
    if (!isInitialized || !shouldShowOSMView) {
      setOsmOverviewData(null);
      return;
    }

    fetchOSMData();

    return () => {
      if (osmAbortControllerRef.current) {
        osmAbortControllerRef.current.abort();
      }
    };
  }, [isInitialized, fetchOSMData, shouldShowOSMView]);

  const formatDateOnly = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const fetchTeamData = useCallback(async () => {
    if (!pipelineService.isReady() || !selectedDepartmentId || selectedDepartmentId === "all") return;

    if (isOfflineMode()) {
      setTeamOverviewData(mockTeamData[selectedDepartmentId] ?? null);
      return;
    }

    if (teamAbortControllerRef.current) {
      teamAbortControllerRef.current.abort();
    }

    teamAbortControllerRef.current = new AbortController();
    const { signal } = teamAbortControllerRef.current;

    setIsDashboardLoading(true);
    try {
      const dateParam = new Date(selectedDate);
      dateParam.setHours(0, 0, 0, 0);
      const data = await pipelineService.getTeamOverview(selectedDepartmentId, formatDateOnly(dateParam), signal);
      setTeamOverviewData(data);
    } catch (err) {
      if (err instanceof Error && err.name === "CanceledError") return;
      console.error("Failed to fetch team overview:", err);
    } finally {
      setIsDashboardLoading(false);
    }
  }, [selectedDepartmentId, selectedDate]);

  useEffect(() => {
    if (!isInitialized || !shouldShowTeamView) {
      setTeamOverviewData(null);
      return;
    }

    fetchTeamData();

    return () => {
      if (teamAbortControllerRef.current) {
        teamAbortControllerRef.current.abort();
      }
    };
  }, [isInitialized, fetchTeamData, shouldShowTeamView]);

  const fetchPinnedApplications = useCallback(async () => {
    if (!pipelineService.isReady() || currentAgentId === -1) return;

    try {
      const pinned = await pipelineService.getPinnedApplications(currentAgentId);
      setPinnedApplications(pinned);
    } catch (err) {
      console.error("Failed to fetch pinned applications:", err);
    }
  }, [currentAgentId]);

  useEffect(() => {
    if (!isInitialized || currentAgentId === -1) return;
    fetchPinnedApplications();
  }, [isInitialized, currentAgentId, fetchPinnedApplications]);

  const handlePinnedApplicationsChange = useCallback(
    async (newPinned: string[]) => {
      setPinnedApplications(newPinned);
      try {
        await pipelineService.savePinnedApplications(currentAgentId, newPinned);
      } catch (err) {
        console.error("Failed to save pinned applications:", err);
      }
    },
    [currentAgentId]
  );

  const isDealerDirectHos = (hierarchy: SalesHierarchyModel | undefined) => {
    if (!hierarchy) {
      return false;
    }

    if (hierarchy.hosId === DEALER_DIRECT_HOS_ID) {
      return true;
    }

    const normalizedHosName = hierarchy.hosName.trim().toLowerCase();
    return normalizedHosName === "dan fildes" || normalizedHosName === "daniel fildes";
  };

  const filteredDirectorData = useMemo(() => {
    if (!hobOverviewData) {
      return null;
    }

    const normalizeKey = (value: string | undefined) => value?.trim().toLowerCase() ?? "";

    const getHierarchyByHosName = (name: string) => {
      const hosNameKey = normalizeKey(name);
      return salesHierarchy.find((h) => normalizeKey(h.hosName) === hosNameKey);
    };

    if (selectedBusiness === "Whole Business") {
      return hobOverviewData;
    }

    const isMatchingBusinessFilter = (name: string) => {
      const hierarchy = getHierarchyByHosName(name);

      if (!hierarchy) {
        return false;
      }

      const isDealerDirect = isDealerDirectHos(hierarchy);

      if (selectedBusiness === "Consumer Direct") {
        return !isDealerDirect;
      }

      if (selectedBusiness === "Dealer Direct") {
        return isDealerDirect;
      }

      return true;
    };

    const docs = hobOverviewData.docs.filter((item) => isMatchingBusinessFilter(item.name));
    const payouts = hobOverviewData.payouts.filter((item) => isMatchingBusinessFilter(item.name));
    const wiPs = hobOverviewData.wiPs.filter((item) => isMatchingBusinessFilter(item.name));
    const allocations = hobOverviewData.allocations?.filter((item) => isMatchingBusinessFilter(item.name));
    const docsPredicted = docs.reduce((sum, item) => sum + item.predicted, 0);
    const docsActual = docs.reduce((sum, item) => sum + item.actual, 0);
    const totalReDoc = docs.reduce((sum, item) => sum + item.totalReDoc, 0);
    const paidPredicted = payouts.reduce((sum, item) => sum + item.predicted, 0);
    const paidActual = payouts.reduce((sum, item) => sum + item.actual, 0);
    const gapDocsOut = wiPs.reduce((sum, item) => sum + item.docsOutLive, 0);
    const gapReadyToGo = wiPs.reduce((sum, item) => sum + item.readyToGoLive, 0);
    const gapPayout = wiPs.reduce((sum, item) => sum + item.payoutLive, 0);

    return {
      ...hobOverviewData,
      docs,
      payouts,
      wiPs,
      allocations,
      statistics: {
        appsTotal: undefined,
        warmAppsTotal: undefined,
        allocatedOnSelectedDayTotal: undefined,
        docsPredicted,
        docsActual,
        paidPredicted,
        paidActual,
        gapDocsOut,
        gapReadyToGo,
        gapPayout,
        gapTotal: gapDocsOut + gapReadyToGo + gapPayout,
        totalReDoc,
      },
    };
  }, [hobOverviewData, salesHierarchy, selectedBusiness]);

  const hobOptions = useMemo(() => {
    if (filteredDirectorData) {
      const uniqueHoBs = new Map<number, string>();
      filteredDirectorData.docs.forEach((d) => {
        const hierarchy = salesHierarchy.find((h) => h.hosName === d.name);
        if (hierarchy) {
          uniqueHoBs.set(hierarchy.hosId, d.name);
        }
      });
      return Array.from(uniqueHoBs.entries()).map(([id, name]) => ({
        value: JSON.stringify({ id, name }),
        label: name,
      }));
    }
    return [];
  }, [filteredDirectorData, salesHierarchy]);

  const osmOptions = useMemo(() => {
    if (!salesHierarchy.length) return [];

    const matchesBusiness = (h: SalesHierarchyModel) => {
      if (selectedBusiness === "Whole Business") return true;
      const isDealerDirect = isDealerDirectHos(h);
      if (selectedBusiness === "Consumer Direct") return !isDealerDirect;
      if (selectedBusiness === "Dealer Direct") return isDealerDirect;
      return true;
    };

    const uniqueOsms = new Map<number, string>();
    salesHierarchy.filter(matchesBusiness).forEach((h) => uniqueOsms.set(h.osmId, h.osmName));
    return Array.from(uniqueOsms.entries()).map(([, name]) => ({
      value: name,
      label: name,
    }));
  }, [salesHierarchy, selectedBusiness]);

  const filteredAgentOptions = useMemo(() => {
    if (!selectedDepartmentId || selectedDepartmentId === "all") {
      return workerOptions;
    }

    return workers
      .filter(
        (worker) => worker.departmentId === selectedDepartmentId && worker?.userId != null && worker?.name != null
      )
      .map((worker) => ({
        value: worker.userId,
        label: worker.name,
      }));
  }, [workers, workerOptions, selectedDepartmentId]);

  const isPredictionForToday = (predictedDate: string | undefined): boolean => {
    if (!predictedDate) return false;

    const todayOnly = formatDateOnly(new Date());
    const predictionDateOnlyMatch = predictedDate.match(/^\d{4}-\d{2}-\d{2}/);
    if (predictionDateOnlyMatch) {
      return predictionDateOnlyMatch[0] === todayOnly;
    }

    const parsedPredictionDate = new Date(predictedDate);
    if (Number.isNaN(parsedPredictionDate.getTime())) {
      return false;
    }

    return formatDateOnly(parsedPredictionDate) === todayOnly;
  };

  const isPredictionOnOrBeforeSelectedDate = (predictedDate: string | undefined, selectedDateValue: Date): boolean => {
    if (!predictedDate) return true;

    const selectedDateOnly = formatDateOnly(selectedDateValue);
    const predictionDateOnlyMatch = predictedDate.match(/^\d{4}-\d{2}-\d{2}/);
    if (predictionDateOnlyMatch) {
      return predictionDateOnlyMatch[0] <= selectedDateOnly;
    }

    const parsedPredictionDate = new Date(predictedDate);
    if (Number.isNaN(parsedPredictionDate.getTime())) {
      return true;
    }

    return formatDateOnly(parsedPredictionDate) <= selectedDateOnly;
  };

  const isDocsOutPredictionStatus = (applicationStatusName: string | undefined): boolean => {
    if (!applicationStatusName) return false;
    return applicationStatusName.trim().toLowerCase() === "docs out";
  };

  const mapDashboardStatistics = (
    stats: DashboardPipelineStatistics,
    predictionsDoc?: DashboardPipelineModel["predictionsDoc"],
    todayOnly = false
  ) => ({
    appsTotal: stats.appsTotal,
    warmAppsTotal: stats.warmAppsTotal,
    allocatedOnSelectedDayTotal: stats.allocatedOnSelectedDayTotal,
    predictedToDocs: Array.isArray(predictionsDoc)
      ? predictionsDoc.filter((prediction) =>
          !isDocsOutPredictionStatus(prediction.applicationStatusName) &&
          (todayOnly
            ? isPredictionForToday(prediction.predictedDate)
            : isPredictionOnOrBeforeSelectedDate(prediction.predictedDate, selectedDate))
        ).length
      : stats.docsPredicted,
    docsToday: stats.docsActual,
    totalReDoc: stats.totalReDoc,
    gapDocsOut: stats.gapDocsOut,
    gapReadyToGo: stats.gapReadyToGo,
    gapPayout: stats.gapPayout,
    gapTotal: stats.gapTotal,
    predictedToPay: stats.paidPredicted,
    paidToday: stats.paidActual,
  });

  const statistics = useMemo(() => {
    if (shouldShowDirectorView && filteredDirectorData) {
      return mapDashboardStatistics(filteredDirectorData.statistics, filteredDirectorData.predictionsDoc);
    }
    if (shouldShowHoBView && hobDetailData) {
      return mapDashboardStatistics(hobDetailData.statistics, hobDetailData.predictionsDoc);
    }
    if (shouldShowOSMView && selectedOSM && osmOverviewData) {
      return mapDashboardStatistics(osmOverviewData.statistics, osmOverviewData.predictionsDoc, true);
    }
    if (shouldShowTeamView && teamOverviewData) {
      return mapDashboardStatistics(teamOverviewData.statistics, teamOverviewData.predictionsDoc, true);
    }
    if (!pipelineData) return null;
    if (pipelineData.type === "all") {
      return pipelineData.data.totalStatistics;
    }
    return pipelineData.data.statistics;
  }, [
    pipelineData,
    shouldShowDirectorView,
    filteredDirectorData,
    shouldShowHoBView,
    hobDetailData,
    shouldShowOSMView,
    selectedOSM,
    osmOverviewData,
    shouldShowTeamView,
    teamOverviewData,
    selectedDate,
  ]);

  const handovers = useMemo<Handover[]>(() => {
    if (!pipelineData || pipelineData.type !== "department") return [];
    return pipelineData.data.handovers || [];
  }, [pipelineData]);

  const valCalls = useMemo<ValCallModel[]>(() => {
    if (!pipelineData || pipelineData.type === "all") return [];
    return pipelineData.data.valCalls || [];
  }, [pipelineData]);

  const wip = useMemo<WIPModel[]>(() => {
    if (!pipelineData || pipelineData.type === "all") return [];
    return pipelineData.data.wip || [];
  }, [pipelineData]);

  const newLeads = useMemo<CustomerModel[]>(() => {
    if (!pipelineData || pipelineData.type === "all") return [];
    return [];
  }, [pipelineData]);

  const pinnedWarmCustomers = useMemo<CustomerModel[]>(() => {
    if (!pipelineData || pipelineData.type === "all") return [];
    return [];
  }, [pipelineData]);

  const warm = useMemo<CustomerModel[]>(() => {
    if (!pipelineData || pipelineData.type === "all") return [];
    return pipelineData.data.customersWarm || [];
  }, [pipelineData]);

  const customers = useMemo<CustomerModel[]>(() => {
    if (!pipelineData || pipelineData.type === "all") return [];
    return pipelineData.data.customers || [];
  }, [pipelineData]);

  const pipelineType = useMemo(() => {
    if (!pipelineData) return "all";
    return pipelineData.type;
  }, [pipelineData]);

  const canGoBack = useMemo(() => {
    return currentLevel !== PipelineLevel.Director;
  }, [currentLevel]);

  const headerTitle = useMemo(() => {
    if (currentLevel === PipelineLevel.Director) {
      return "All Heads of Sales";
    }

    if (currentLevel === PipelineLevel.HoB) {
      return selectedHeadOfBusiness?.name ?? "";
    }

    if (currentLevel === PipelineLevel.OSM) {
      return selectedOSM ?? "";
    }

    if (currentLevel === PipelineLevel.Team) {
      if (selectedDepartmentId && selectedDepartmentId !== "all") {
        const department = departmentOptions.find((dept) => dept.value === selectedDepartmentId);
        if (department) {
          return department.label;
        }
      }
      return "All Teams";
    }

    if (currentLevel === PipelineLevel.Agent) {
      if (selectedAgentId !== null && selectedAgentId !== -1) {
        const agent = workerOptions.find((a) => a.value === selectedAgentId);
        if (agent) {
          return agent.label;
        }
      }
      return "Agent";
    }

    return "Pipeline";
  }, [
    currentLevel,
    selectedHeadOfBusiness,
    selectedOSM,
    selectedDepartmentId,
    selectedAgentId,
    departmentOptions,
    workerOptions,
  ]);

  const handleMyQueueClick = () => {
    setSelectedDepartmentId(currentAgentDepartmentId);
    setSelectedAgentId(currentAgentId);
    setSelectedHeadOfBusiness(null);
    setSelectedOSM(null);
    setSelectedBusiness(DEFAULT_BUSINESS_OPTIONS[0].value as BusinessFilterOption);
  };

  const handleBackClick = () => {
    if (selectedAgentId !== -1) {
      setSelectedAgentId(-1);
      return;
    }

    if (selectedDepartmentId && selectedDepartmentId !== "all") {
      const osmName = getOSMForDepartment(selectedDepartmentId);
      setSelectedDepartmentId("all");
      setSelectedAgentId(-1);
      setSelectedOSM(osmName);
      return;
    }

    if (selectedOSM) {
      const hob = getHoBForOSM(selectedOSM);
      setSelectedOSM(null);
      setSelectedHeadOfBusiness(hob);
      return;
    }

    if (selectedHeadOfBusiness) {
      setSelectedHeadOfBusiness(null);
      setSelectedAgentId(-1);
    }
  };

  const handleTeamSelect = (value: string) => {
    setSelectedDepartmentId(value);
    setSelectedAgentId(-1);
  };

  const handleAgentSelect = (value: number) => {
    setSelectedAgentId(value);
  };

  const handleDepartmentNameClick = (departmentName: string) => {
    const department = departmentOptions.find((dept) => dept.label === departmentName);
    if (department) {
      setSelectedDepartmentId(department.value);
      setSelectedAgentId(-1);
    }
  };

  const handleOSMNameClick = (osmName: string) => {
    if (!osmName) return;
    setSelectedOSM(osmName);
  };

  const handleHeadOfBusinessNameClick = (hobName: string) => {
    if (!hobName) return;
    const hierarchy = salesHierarchy.find((h) => h.hosName === hobName);
    if (hierarchy) {
      setSelectedHeadOfBusiness({
        id: hierarchy.hosId,
        name: hobName,
      });
    }
  };

  const BreakdownViewProps = useMemo(() => {
    return {
      selectedDepartmentId,
      selectedAgentId: normalizedSelectedAgentId,
      pipelineType,
      statistics,
      handovers,
      valCalls,
      wip,
      newLeads,
      pinnedWarmCustomers,
      warm,
      customers,
      currentAgentId,
      pinnedApplications,
      setPinnedApplications: handlePinnedApplicationsChange,
      setSelectedAgentId,
      selectedDate,
    };
  }, [
    selectedDepartmentId,
    normalizedSelectedAgentId,
    pipelineType,
    statistics,
    handovers,
    valCalls,
    wip,
    newLeads,
    pinnedWarmCustomers,
    warm,
    customers,
    pinnedApplications,
    currentAgentId,
    handlePinnedApplicationsChange,
    selectedDate,
  ]);

  const SummaryViewProps = useMemo(() => {
    return {
      pipelineData,
      statistics,
      shouldShowDirectorView,
      shouldShowHoBView,
      shouldShowOSMView,
      shouldShowTeamView,
      selectedHeadOfBusiness,
      selectedOSM,
      selectedDepartmentId,
      hobOverviewData: filteredDirectorData,
      hobDetailData,
      osmOverviewData,
      teamOverviewData,
      selectedDate,
      onDepartmentNameClick: handleDepartmentNameClick,
      onHeadOfBusinessNameClick: handleHeadOfBusinessNameClick,
      onOSMNameClick: handleOSMNameClick,
      onAgentNameClick: handleAgentSelect,
    };
  }, [
    pipelineData,
    statistics,
    shouldShowDirectorView,
    shouldShowHoBView,
    shouldShowOSMView,
    shouldShowTeamView,
    selectedHeadOfBusiness,
    selectedOSM,
    selectedDepartmentId,
    filteredDirectorData,
    hobDetailData,
    osmOverviewData,
    teamOverviewData,
    selectedDate,
    handleDepartmentNameClick,
  ]);

  return (
    <div className="flex flex-grow flex-col overflow-hidden">
      {!isAppDataLoading && (
        <PipelineHeader
          onMyQueueClick={handleMyQueueClick}
          isCurrentAgentView={isCurrentAgentView}
          onBackClick={canGoBack ? handleBackClick : undefined}
          title={headerTitle}
          currentLevel={currentLevel}
          activeTab={activeTab}
          onTabChange={(tab) => {
            setActiveTab(tab);
            sessionStorage.setItem(PIPELINE_VIEW_TAB_KEY, tab);
          }}
          selectedBusiness={selectedBusiness}
          setSelectedBusiness={(val) => {
            if (DEFAULT_BUSINESS_OPTIONS.some((option) => option.value === val)) {
              setSelectedBusiness(val as BusinessFilterOption);
            }
          }}
          businessOptions={DEFAULT_BUSINESS_OPTIONS}
          selectedHoB={selectedHeadOfBusiness ? JSON.stringify(selectedHeadOfBusiness) : null}
          setSelectedHoB={(val) => {
            if (!val) {
              setSelectedHeadOfBusiness(null);
              setSelectedOSM(null);
            } else {
              try {
                setSelectedHeadOfBusiness(JSON.parse(val));
                setSelectedOSM(null);
              } catch {
                setSelectedHeadOfBusiness(null);
                setSelectedOSM(null);
              }
            }
          }}
          hobOptions={hobOptions}
          selectedOSM={selectedOSM}
          setSelectedOSM={(val: string | null) => {
            setSelectedOSM(val);
            if (!val) {
              setSelectedDepartmentId("all");
              setSelectedAgentId(-1);
            }
          }}
          osmOptions={osmOptions}
          selectedTeamId={selectedDepartmentId}
          setSelectedTeamId={handleTeamSelect}
          teamOptions={departmentOptions}
          selectedAgentId={normalizedSelectedAgentId}
          setSelectedAgentId={handleAgentSelect}
          agentOptions={filteredAgentOptions}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedDate={selectedDate}
          onDateChange={setSelectedDate}
        />
      )}

      {isLoading ||
      (isDashboardLoading &&
        (shouldShowDirectorView || shouldShowHoBView || shouldShowOSMView || shouldShowTeamView)) ? (
        <div className="flex flex-grow items-center justify-center">
          <Spinner />
        </div>
      ) : error ? (
        <div className="flex flex-grow flex-col items-center justify-center gap-2">
          <CircleX className="stroke-1 text-rose-700" size={40} />
          <p className="text-sm text-rose-700">{error}</p>
          <Button onClick={fetchPipelineData} variant="outline">
            Try again
          </Button>
        </div>
      ) : currentLevel === PipelineLevel.Agent ? (
        <BreakdownView {...BreakdownViewProps} />
      ) : currentLevel === PipelineLevel.Director ||
        currentLevel === PipelineLevel.HoB ||
        currentLevel === PipelineLevel.OSM ? (
        <SummaryView {...SummaryViewProps} />
      ) : activeTab === "summary" ? (
        <SummaryView {...SummaryViewProps} />
      ) : (
        <BreakdownView {...BreakdownViewProps} />
      )}
    </div>
  );
};
